import { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { X, FileSpreadsheet, Printer, Search, Clock, CheckCircle, MapPin, ClipboardList } from 'lucide-react';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { useStore, type TimeSlot } from '../store/useStore';

// ─── Types ────────────────────────────────────────────────────────────────────

type ContentType = 'points' | 'tasks' | 'both';

interface ExportItem {
  id: string;
  name: string;
  type: 'point' | 'task';
  categoryId?: string;
  iconName?: string;
  openingHours: TimeSlot[];
}

interface OperatingHoursExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatUTCTime(isoStr: string): string {
  if (!isoStr) return '';
  const parts = isoStr.split('T');
  if (parts.length < 2) return '';
  return parts[1].substring(0, 5);
}

function formatDayTabLabel(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('fr-FR', {
    weekday: 'short', day: 'numeric', month: 'short'
  });
}

function formatDateShort(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}`;
}

const EMOJI_MAP: Record<string, string> = {
  MapPin: '📍', MapPinIcon: '📍', Tent: '⛺', Music: '🎵', Utensils: '🍴',
  Coffee: '☕', Car: '🚗', Info: 'ℹ️', Ticket: '🎫', Star: '⭐',
  Shield: '🛡️', Store: '🏪', PlugConnected: '🔌', Drum: '🥁', Soup: '🥣',
  TruckElectric: '🚚', Passport: '🛂', Player: '🎮', Heart: '❤️',
  Camera: '📷', Wifi: '📶', Battery: '🔋', Flame: '🔥', Trash: '🗑️',
  Users: '👥', Cart: '🛒', Bell: '🔔', Magnifier: '🔍', MessageCircle: '💬',
  Wallet: '👛', CreditCard: '💳', CurrencyDollar: '💵', Accessibility: '♿',
  BrandBagsFm: '🛍️', Banana: '🍌', CameraOff: '🚫📷', InfoCircle: 'ℹ️',
  UsersGroup: '👥', UserPlus: '➕👤', UserCheck: '✔️', Logout: '🚪',
  LetterP: '🅿️', ShieldCheck: '🛡️✔️', Beer: '🍺', Wine: '🍷',
  Glasses: '👓', Radio: '📻', Tv: '📺', Key: '🔑', Hammer: '🔨',
  Bus: '🚌', AlertTriangle: '⚠️', Cloud1: '☁️', Cloud1Icon: '☁️',
};

function getIconEmoji(iconName?: string): string {
  if (!iconName) return '📍';
  return EMOJI_MAP[iconName] || '📍';
}

/** Group slots by date, merging to earliest start / latest end per day */
function groupSlotsByDate(slots: TimeSlot[]): Record<string, { start: string; end: string; endNextDay: boolean }[]> {
  const hoursByDate: Record<string, { start: string; end: string; endNextDay: boolean }[]> = {};
  if (!slots || slots.length === 0) return hoursByDate;

  const rawByDate: Record<string, { start: string; end: string; startFull: string; endFull: string }[]> = {};
  slots.forEach(h => {
    if (!h.start || !h.end) return;
    const date = h.start.split('T')[0];
    if (!rawByDate[date]) rawByDate[date] = [];
    rawByDate[date].push({ start: formatUTCTime(h.start), end: formatUTCTime(h.end), startFull: h.start, endFull: h.end });
  });

  Object.entries(rawByDate).forEach(([date, dateSlots]) => {
    dateSlots.sort((a, b) => a.startFull.localeCompare(b.startFull));
    const earliest = dateSlots[0];
    const latest = dateSlots.reduce((max, s) => s.endFull > max.endFull ? s : max, dateSlots[0]);
    const startDate = earliest.startFull.split('T')[0];
    const endDate = latest.endFull.split('T')[0];
    hoursByDate[date] = [{ start: earliest.start, end: latest.end, endNextDay: endDate !== startDate }];
  });

  return hoursByDate;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function OperatingHoursExportModal({ isOpen, onClose }: OperatingHoursExportModalProps) {
  const { points, allPoints, tasks, categories } = useVisibleStore();
  const { globalSettings, projects, currentProjectId } = useStore();

  const [contentType, setContentType] = useState<ContentType>('points');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);

  const appLogo = globalSettings?.appLogoUrl || '';
  const project = projects.find(p => p.id === currentProjectId);
  const projectName = project?.name || 'Projet';

  // ── Derived: unified export items ──────────────────────────────────────────
  const items: ExportItem[] = useMemo(() => {
    const result: ExportItem[] = [];
    const visiblePoints = allPoints.length > 0 ? allPoints : points;

    if (contentType === 'points' || contentType === 'both') {
      visiblePoints.forEach(p => {
        result.push({
          id: p.id, name: p.name, type: 'point',
          categoryId: p.categoryId, iconName: p.iconName,
          openingHours: p.openingHours || [],
        });
      });
    }
    if (contentType === 'tasks' || contentType === 'both') {
      tasks.forEach(t => {
        result.push({
          id: t.id, name: t.name, type: 'task',
          categoryId: t.categoryId,
          openingHours: (t as any).openingHours || [],
        });
      });
    }
    return result.sort((a, b) => a.name.localeCompare(b.name));
  }, [contentType, points, allPoints, tasks]);

  // ── Derived: filtered by search ────────────────────────────────────────────
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(item => {
      const cat = categories.find(c => c.id === item.categoryId);
      return item.name.toLowerCase().includes(q) ||
        (cat?.name && cat.name.toLowerCase().includes(q));
    });
  }, [items, searchQuery, categories]);

  // ── Derived: selected items ────────────────────────────────────────────────
  const selectedItems = useMemo(
    () => items.filter(item => selectedIds.has(item.id)),
    [items, selectedIds]
  );

  // ── Derived: all unique dates across selected items ────────────────────────
  const allDates = useMemo(() => {
    const dates = new Set<string>();
    selectedItems.forEach(item => {
      item.openingHours.forEach(slot => {
        if (slot.start) dates.add(slot.start.split('T')[0]);
        if (slot.end) dates.add(slot.end.split('T')[0]);
      });
    });
    return Array.from(dates).sort();
  }, [selectedItems]);

  // ── Derived: hours by item by date ─────────────────────────────────────────
  const hoursData = useMemo(() => {
    return selectedItems.map(item => ({
      item,
      hoursByDate: groupSlotsByDate(item.openingHours),
    }));
  }, [selectedItems]);

  // ── Selection helpers ──────────────────────────────────────────────────────
  const toggleItem = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(filteredItems.map(i => i.id)));
  const deselectAll = () => setSelectedIds(new Set());

  // ── Reset selection when contentType changes ───────────────────────────────
  const handleContentTypeChange = (type: ContentType) => {
    setContentType(type);
    setSelectedIds(new Set());
    setSearchQuery('');
  };

  // ── PDF Generation ─────────────────────────────────────────────────────────
  const generatePrintHtml = (): string => {
    if (selectedItems.length === 0 || allDates.length === 0) {
      return '<p style="padding:40px;text-align:center;font-family:sans-serif;">Aucun élément ou aucune date sélectionné.</p>';
    }

    const logoHtml = appLogo
      ? `<img src="${appLogo}" style="height:28px;width:auto;object-fit:contain;margin-right:10px;" />`
      : '';

    // Build table rows
    let tableRows = '';
    hoursData.forEach(({ item, hoursByDate }, rowIdx) => {
      const cat = categories.find(c => c.id === item.categoryId);
      const bg = rowIdx % 2 === 0 ? '#ffffff' : '#f8fafc';
      const iconEmoji = item.type === 'point' ? getIconEmoji(item.iconName) : '📋';

      let cells = '';
      allDates.forEach(date => {
        const daySlots = hoursByDate[date];
        if (daySlots && daySlots.length > 0) {
          const slotLines = daySlots.map(s =>
            `${s.start} → ${s.end}${s.endNextDay ? ' (+1)' : ''}`
          ).join('<br>');
          cells += `<td style="padding:4px 6px;border:1px solid #e2e8f0;font-size:9px;font-family:monospace;text-align:center;vertical-align:middle;background:${bg};">${slotLines}</td>`;
        } else {
          cells += `<td style="padding:4px 6px;border:1px solid #e2e8f0;font-size:9px;text-align:center;color:#94a3b8;vertical-align:middle;background:${bg};">—</td>`;
        }
      });

      tableRows += `
        <tr>
          <td style="padding:4px 8px;border:1px solid #e2e8f0;font-size:10px;background:${bg};white-space:nowrap;">
            <span style="font-weight:700;">${iconEmoji} ${item.name}</span>
            ${cat ? `<br><span style="font-size:8px;color:#64748b;">${cat.name}</span>` : ''}
            <br><span style="font-size:7px;color:#94a3b8;text-transform:uppercase;">${item.type === 'point' ? 'Point' : 'Tâche'}</span>
          </td>
          ${cells}
        </tr>`;
    });

    // Build date header
    const dateHeaders = allDates.map(date => {
      const label = formatDayTabLabel(date);
      return `<th style="padding:6px 4px;background:#1e293b;color:#fff;font-size:9px;font-weight:700;text-align:center;border:1px solid #1e293b;white-space:nowrap;">${label}</th>`;
    }).join('');

    const dateRangeText = allDates.length > 0
      ? `Du ${formatDateShort(allDates[0])} au ${formatDateShort(allDates[allDates.length - 1])}`
      : '';

    return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>Horaires — ${projectName}</title>
  <style>
    @page { size: A4 landscape; margin: 10mm 12mm; }
    * { box-sizing: border-box; }
    body { font-family: system-ui, -apple-system, sans-serif; color: #0f172a; background: #fff; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    table { border-collapse: collapse; width: 100%; }
    @media print { body { margin: 0; } }
  </style>
</head>
<body>
  <div style="padding:0;">
    <!-- Header -->
    <div style="display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-bottom:2px solid #1e293b;margin-bottom:10px;">
      <div style="display:flex;align-items:center;">
        ${logoHtml}
        <div>
          <div style="font-size:15px;font-weight:800;color:#0f172a;">${projectName} — Horaires d'Ouverture</div>
          <div style="font-size:9px;color:#64748b;margin-top:1px;">${selectedItems.length} élément(s) · ${allDates.length} jour(s) · ${dateRangeText}</div>
        </div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:9px;color:#94a3b8;">Généré le ${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
      </div>
    </div>

    <!-- Grid Table -->
    <table>
      <thead>
        <tr>
          <th style="padding:6px 8px;background:#0f172a;color:#fff;font-size:10px;text-align:left;border:1px solid #0f172a;min-width:120px;">Point / Tâche</th>
          ${dateHeaders}
        </tr>
      </thead>
      <tbody>
        ${tableRows}
      </tbody>
    </table>

    <!-- Footer -->
    <div style="margin-top:10px;border-top:1px solid #e2e8f0;padding-top:5px;display:flex;justify-content:space-between;font-size:7px;color:#94a3b8;">
      <span>${projectName} — Usage interne</span>
      <span>Horaires d'ouverture</span>
    </div>
  </div>
</body>
</html>`;
  };

  const handlePrintPreview = () => {
    if (selectedItems.length === 0) return;
    setIsGeneratingPdf(true);
    try {
      const html = generatePrintHtml();
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentWindow?.document || iframe.contentDocument;
      if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(html);
        iframeDoc.close();
        iframe.contentWindow?.focus();
        setTimeout(() => {
          iframe.contentWindow?.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 2000);
        }, 600);
      }
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // ── Excel Generation ───────────────────────────────────────────────────────
  const handleExcelExport = async () => {
    if (selectedItems.length === 0) return;
    setIsGeneratingExcel(true);
    try {
      const wb = XLSX.utils.book_new();
      const sheetData: any[][] = [];

      // Row 0: Title
      sheetData.push([`${projectName} — Horaires d'Ouverture`, '', '', '', '', '', '']);
      // Row 1: spacer
      sheetData.push(['']);
      // Row 2: Headers
      sheetData.push(['Type', 'Nom', 'Catégorie', 'Date', 'Heure Début', 'Heure Fin', 'Durée (h)']);

      // Rows: one per (item, date, slot)
      hoursData.forEach(({ item, hoursByDate }) => {
        const cat = categories.find(c => c.id === item.categoryId);
        const typeLabel = item.type === 'point' ? 'Point' : 'Tâche';
        const sortedDates = Object.keys(hoursByDate).sort();

        if (sortedDates.length === 0) {
          // Item with no hours
          sheetData.push([typeLabel, item.name, cat?.name || '—', '—', '—', '—', 0]);
        } else {
          sortedDates.forEach(date => {
            const daySlots = hoursByDate[date];
            if (daySlots.length === 0) {
              sheetData.push([typeLabel, item.name, cat?.name || '—', formatDateShort(date), '—', '—', 0]);
            } else {
              daySlots.forEach(s => {
                const startTime = s.start;
                const endTime = s.end;
                // Calculate duration
                const startParts = startTime.split(':').map(Number);
                const endParts = endTime.split(':').map(Number);
                let duration = (endParts[0] + endParts[1] / 60) - (startParts[0] + startParts[1] / 60);
                if (s.endNextDay) duration += 24; // overnight
                const durationRounded = Math.round(duration * 10) / 10;

                sheetData.push([
                  typeLabel,
                  item.name,
                  cat?.name || '—',
                  formatDateShort(date),
                  startTime,
                  s.endNextDay ? `${endTime} (+1)` : endTime,
                  durationRounded,
                ]);
              });
            }
          });
        }
      });

      const ws = XLSX.utils.aoa_to_sheet(sheetData);

      // Styling
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
      for (let R = range.s.r; R <= range.e.r; R++) {
        for (let C = range.s.c; C <= range.e.c; C++) {
          const cellAddr = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[cellAddr]) { ws[cellAddr] = { v: '', t: 's' }; }
          if (!ws[cellAddr].s) { (ws[cellAddr] as any).s = {}; }

          if (R === 0) {
            // Title row
            (ws[cellAddr] as any).s = {
              font: { bold: true, sz: 13, color: { argb: 'FFFFFFFF' } },
              fill: { type: 'pattern', patternType: 'solid', fgColor: { argb: 'FF0F172A' } },
            };
          } else if (R === 2) {
            // Header row
            (ws[cellAddr] as any).s = {
              font: { bold: true, sz: 10, color: { argb: 'FFFFFFFF' } },
              fill: { type: 'pattern', patternType: 'solid', fgColor: { argb: 'FF1E293B' } },
            };
          } else if (R > 2) {
            // Data rows - alternate striping
            const bg = (R % 2 === 1) ? 'FFFFFFFF' : 'FFF8FAFC';
            (ws[cellAddr] as any).s = {
              fill: { type: 'pattern', patternType: 'solid', fgColor: { argb: bg } },
              font: { sz: 9 },
            };
          }
        }
      }

      // Column widths
      ws['!cols'] = [
        { wch: 8 },   // Type
        { wch: 30 },  // Nom
        { wch: 20 },  // Catégorie
        { wch: 12 },  // Date
        { wch: 14 },  // Heure Début
        { wch: 14 },  // Heure Fin
        { wch: 10 },  // Durée
      ];

      // Merge title row across all columns
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }];

      // Auto-filter
      ws['!autofilter'] = { ref: `A3:G${sheetData.length}` };

      XLSX.utils.book_append_sheet(wb, ws, 'Horaires');
      const safeName = projectName.replace(/[^a-zA-Z0-9]/g, '_');
      XLSX.writeFile(wb, `horaires_ouverture_${safeName}.xlsx`);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  // ── Early return ───────────────────────────────────────────────────────────
  if (!isOpen) return null;

  const hasSelection = selectedItems.length > 0;
  const hasHours = hoursData.some(d => Object.keys(d.hoursByDate).length > 0);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-[130]">
      <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">

        {/* Header */}
        <div className="p-5 border-b border-[#2D3139] flex justify-between items-center bg-[#1A1D23] flex-shrink-0">
          <h3 className="font-bold text-white flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
              <Clock className="w-4 h-4 text-indigo-400" />
            </span>
            Export Horaires d'Ouverture
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-[#2D3139]">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1 custom-scrollbar">

          {/* Content type toggle */}
          <div className="flex gap-2 p-1 bg-[#0A0B0E] rounded-xl">
            <button
              onClick={() => handleContentTypeChange('points')}
              className={`flex-1 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-all ${contentType === 'points' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
            >
              <MapPin className="w-3.5 h-3.5" /> Points
            </button>
            <button
              onClick={() => handleContentTypeChange('tasks')}
              className={`flex-1 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-all ${contentType === 'tasks' ? 'bg-emerald-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
            >
              <ClipboardList className="w-3.5 h-3.5" /> Tâches
            </button>
            <button
              onClick={() => handleContentTypeChange('both')}
              className={`flex-1 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-all ${contentType === 'both' ? 'bg-purple-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
            >
              <CheckCircle className="w-3.5 h-3.5" /> Les deux
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Filtrer les éléments..."
              className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition-colors"
            />
          </div>

          {/* Selection controls */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">
              {filteredItems.length} élément{filteredItems.length !== 1 ? 's' : ''}
            </span>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="text-[10px] font-bold text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 px-2.5 py-1 rounded-lg transition-colors"
              >
                Tout sélectionner
              </button>
              <button
                onClick={deselectAll}
                className="text-[10px] font-bold text-slate-400 hover:text-white bg-slate-500/10 hover:bg-slate-500/20 px-2.5 py-1 rounded-lg transition-colors"
              >
                Désélectionner
              </button>
            </div>
          </div>

          {/* Items list */}
          <div className="max-h-56 overflow-y-auto custom-scrollbar space-y-1 border border-[#2D3139] rounded-xl bg-[#0A0B0E]/60 p-2">
            {filteredItems.length === 0 ? (
              <p className="text-center text-slate-500 text-xs py-8">
                {searchQuery ? 'Aucun élément ne correspond à votre recherche.' : 'Aucun élément disponible.'}
              </p>
            ) : (
              filteredItems.map(item => {
                const cat = categories.find(c => c.id === item.categoryId);
                const isSelected = selectedIds.has(item.id);
                const hasHours = (item.openingHours || []).length > 0;
                return (
                  <label
                    key={item.id}
                    className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all border ${
                      isSelected
                        ? 'bg-indigo-500/10 border-indigo-500/30'
                        : 'border-transparent hover:bg-[#1A1D23] hover:border-[#2D3139]'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleItem(item.id)}
                      className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-indigo-500 focus:ring-indigo-500/20 flex-shrink-0"
                    />
                    <span className="text-base flex-shrink-0">
                      {item.type === 'point' ? getIconEmoji(item.iconName) : '📋'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white truncate">{item.name}</span>
                        {!hasHours && (
                          <span className="text-[9px] text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded flex-shrink-0" title="Aucun horaire défini">
                            ⚠️ Sans horaires
                          </span>
                        )}
                      </div>
                      {cat && (
                        <span className="text-[10px] text-slate-500">{cat.name}</span>
                      )}
                    </div>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                      item.type === 'point'
                        ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20'
                        : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                    }`}>
                      {item.type === 'point' ? 'Point' : 'Tâche'}
                    </span>
                  </label>
                );
              })
            )}
          </div>

          {/* Selection summary */}
          <div className="bg-[#0A0B0E]/60 border border-[#2D3139]/60 rounded-xl p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-400">
                <span className="text-white font-bold">{selectedItems.length}</span> élément{selectedItems.length !== 1 ? 's' : ''} sélectionné{selectedItems.length !== 1 ? 's' : ''}
              </span>
              {hasSelection && (
                <span className="text-xs text-slate-400">
                  · <span className="text-white font-bold">{allDates.length}</span> date{allDates.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            {!hasHours && hasSelection && (
              <span className="text-[10px] text-amber-400">⚠️ Les éléments sélectionnés n'ont pas d'horaires définis</span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-[#2D3139] flex gap-3 flex-shrink-0">
          <button
            onClick={handlePrintPreview}
            disabled={!hasSelection || isGeneratingPdf}
            className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl transition-all shadow-lg shadow-indigo-500/20 text-sm font-bold flex items-center justify-center gap-2"
          >
            {isGeneratingPdf ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Printer className="w-4 h-4" />
            )}
            PDF Imprimer
          </button>
          <button
            onClick={handleExcelExport}
            disabled={!hasSelection || isGeneratingExcel}
            className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl transition-all shadow-lg shadow-emerald-500/20 text-sm font-bold flex items-center justify-center gap-2"
          >
            {isGeneratingExcel ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4" />
            )}
            Excel .xlsx
          </button>
        </div>

      </div>
    </div>
  );
}
